import { Link } from "react-router-dom";

import { useDashboardDistressedSignals } from "@/api/dashboard";

/**
 * Communities whose ADVERT TEXT carries the most urgency (MPPI-00850).
 *
 * Renamed from "Distressed Signals" because that is what the distressed
 * digest and its pages are called, and the two measure different things:
 * this counts what the SELLER WROTE, the digest counts how the PRICE
 * BEHAVED. Measured 2026-10-19 on active stock — 1,780 properties here,
 * 11,323 there, **362 in both**. A community at the top of this table will
 * usually not be on the distressed list, and under one name that reads as
 * a contradiction rather than as two measures.
 *
 * **The row count and the page it opens are close but not identical, on
 * purpose.** This panel collapses duplicate adverts (`not_duplicate_clause`)
 * and limits itself to price-reliable sources; `/properties` deliberately
 * shows every advert, because an operator working a list wants to see all
 * of them. Measured 2026-10-19: Tilal City reads 131 here and opens 159
 * there, the difference being 28 duplicate adverts. Before MPPI-00852 the
 * same click opened **4,657** — every property in the community, with no
 * wording filter at all — so the link now describes the right population
 * even though the two counts are not to the unit.
 *
 * Heat-style table of communities carrying the most such signals. The
 * intensity shading is driven by the total so the eye finds hot spots without
 * a real heatmap library.
 *
 * Rows link into /properties filtered to that community (MPPI-00061) — the
 * panel names a place worth looking at, and the obvious next question is
 * which properties. Emirate travels with the community because names repeat
 * across emirates; without it a click on Ajman Downtown could pull in a
 * same-named community elsewhere.
 *
 * Counts verified against source rows on 2026-08-15 (MPPI-00060): 127
 * community groups qualify, the top 50 summing to 722 properties, and sampled
 * quotes are genuine distress language ("Very motivated seller", "PRICE
 * NEGOTIABLE", "cash only"), not marketing copy.
 */
export function DistressedSignalsPanel() {
  const { data, isLoading } = useDashboardDistressedSignals(12);
  const max = data?.reduce((m, r) => Math.max(m, r.total), 0) ?? 1;

  return (
    // Same fixed height as PriceDropsPanel beside it (MPPI-00653), scrolled
    // internally so the pair stay level however many rows each holds.
    <div className="flex h-[26rem] flex-col rounded-lg border bg-white p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Seller Wording Signals</h3>
        <span className="text-[10px] text-gray-500">
          urgency in the advert, not price moves · secondary &amp; ready
        </span>
      </div>
      {isLoading && (
        <p className="py-6 text-center text-xs text-gray-400">Loading…</p>
      )}
      {!isLoading && (data?.length ?? 0) === 0 && (
        <p className="py-6 text-center text-xs text-gray-400">
          No seller-wording signals detected.
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white text-left text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="py-1">Community</th>
              <th className="py-1 text-right">High</th>
              <th className="py-1 text-right">Medium</th>
              <th className="py-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((r, i) => {
              const intensity = Math.min(1, r.total / max);
              const bg = `rgba(239,68,68,${(intensity * 0.25).toFixed(2)})`;
              return (
                <tr
                  key={`${r.community}-${i}`}
                  style={{ background: bg }}
                  className="hover:brightness-95"
                >
                  <td className="py-1.5 pr-2 font-medium text-gray-900">
                    {r.community ? (
                      <Link
                        to={{
                          pathname: "/properties",
                          search: new URLSearchParams({
                            community: r.community,
                            ...(r.emirate ? { emirate: r.emirate } : {}),
                            // The same threshold this panel counts on
                            // (MPPI-00852). Without it the row read 131 and
                            // opened a page of 4,657 — the link went to the
                            // community, not to the properties the number
                            // described.
                            min_seller_wording_score: "0.35",
                          }).toString(),
                        }}
                        className="hover:text-primary hover:underline"
                        title={`Show properties in ${r.community}`}
                      >
                        {r.community}
                      </Link>
                    ) : (
                      "—"
                    )}
                    <span className="ml-1 text-[10px] text-gray-500">
                      {r.emirate ?? ""}
                    </span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-rose-700">
                    {r.high}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-amber-700">
                    {r.medium}
                  </td>
                  <td
                    className="py-1.5 text-right font-semibold tabular-nums"
                    title={
                      "Distinct properties, duplicate adverts collapsed. " +
                      "Opening this row shows every advert, so the page " +
                      "will list somewhat more than this number."
                    }
                  >
                    {r.total}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
